import { secureRandom } from '@/lib/secureRandom';
import { create } from 'zustand';
import apiClient from '../lib/api';
import toast from 'react-hot-toast';

export interface WorkflowTransition {
  id: string;
  label: string;
  fromState: string;
  toState: string;
  buttonStyle?: 'primary' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';
  icon?: string;
  actionHandler?: string;
  requiresNote?: boolean;
}

export interface Workflow {
  id: string;
  type: string;
  description: string;
  initial_status: string;
  states: string[];
  transitions: WorkflowTransition[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const STORAGE_KEY = 'teamone_support_workflows_v7';

export function normalizeTicketType(ticketType?: string): string {
  const clean = (ticketType || 'incident').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (clean === 'request' || clean.includes('purchaserequest') || clean.includes('procure') || clean === 'req') return 'request';
  if (clean.includes('problemrecord') || clean.includes('prb')) return 'problem-record';
  if (clean.includes('changerequests') || clean.includes('chg')) return 'change-requests';
  if (clean.includes('custom') || clean.includes('cust')) return 'custom-type';
  if (clean.includes('serv') || clean.includes('sr')) return 'service-request';
  if (clean.includes('prob') || clean.includes('bug')) return 'problem';
  if (clean.includes('change') || clean.includes('chan') || clean.includes('cab')) return 'change';
  if (clean.includes('acc')) return 'access';
  if (clean.includes('feat')) return 'feature';
  if (clean.includes('inc') || clean.includes('fault') || clean.includes('ticket')) return 'incident';
  return clean || 'incident';
}

export function normalizeStateCategory(state?: string): string {
  const clean = (state || 'new').toLowerCase().replace(/[^a-z0-9]/g, '');
  
  if (
    clean.includes('close') ||
    clean.includes('archiv') ||
    clean.includes('reject') ||
    clean.includes('cancel') ||
    clean.includes('duplicate')
  ) {
    return 'closed';
  }
  
  if (
    clean.includes('resolv') ||
    clean.includes('fulfill') ||
    clean.includes('verif') ||
    clean.includes('fix')
  ) {
    return 'resolved';
  }

  if (clean.includes('escalat')) {
    return 'escalated';
  }

  if (
    clean.includes('suppl') ||
    clean.includes('vendor') ||
    clean.includes('rma') ||
    clean.includes('oem')
  ) {
    return 'pending_vendor';
  }

  if (
    clean.includes('custom') ||
    clean.includes('user') ||
    clean.includes('clarif') ||
    clean.includes('requester')
  ) {
    return 'pending_customer';
  }

  if (
    clean.includes('approv') ||
    clean.includes('cab') ||
    clean.includes('hold') ||
    clean.includes('pending')
  ) {
    return 'pending';
  }

  if (clean.includes('knownerror') || clean.includes('workaround')) {
    return 'knownerror';
  }

  if (clean.includes('schedul')) {
    return 'scheduled';
  }

  if (clean.includes('implement') || clean.includes('deploy')) {
    return 'implemented';
  }

  if (
    clean.includes('progress') ||
    clean.includes('investig') ||
    clean.includes('work') ||
    clean.includes('execut') ||
    clean.includes('active')
  ) {
    return 'inprogress';
  }

  if (clean.includes('assign')) {
    return 'assigned';
  }

  if (
    clean.includes('new') ||
    clean.includes('triage') ||
    clean.includes('open') ||
    clean.includes('draft') ||
    clean.includes('initial')
  ) {
    return 'new';
  }

  return clean;
}

export const INITIAL_WORKFLOWS: Workflow[] = [
  {
    id: 'wf-1',
    type: 'Incident',
    description: 'ITIL structured incident resolution lifecycle from triage through active investigation and closure.',
    initial_status: 'New',
    states: ['New', 'Assigned', 'In Progress', 'Pending Customer', 'Pending Vendor', 'Escalated', 'Resolved', 'Closed'],
    transitions: [
      // 1. New Actions
      { id: 'tr-inc-1', label: 'Assign', fromState: 'New', toState: 'Assigned', buttonStyle: 'primary', icon: 'UserCheck', actionHandler: 'assign' },
      { id: 'tr-inc-2', label: 'Start Work', fromState: 'New', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-inc-3', label: 'Escalate', fromState: 'New', toState: 'Escalated', buttonStyle: 'destructive', icon: 'AlertTriangle', actionHandler: 'escalate' },
      { id: 'tr-inc-4', label: 'Add Note', fromState: 'New', toState: 'New', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-inc-5', label: 'Request Information', fromState: 'New', toState: 'Pending Customer', buttonStyle: 'warning', icon: 'Mail', actionHandler: 'emailUser', requiresNote: true },
      { id: 'tr-inc-6', label: 'Cancel', fromState: 'New', toState: 'Closed', buttonStyle: 'outline', icon: 'X', actionHandler: 'closeFault' },

      // 2. Assigned / In Progress Actions
      { id: 'tr-inc-7', label: 'Change Assignee', fromState: 'In Progress', toState: 'Assigned', buttonStyle: 'secondary', icon: 'UserPlus', actionHandler: 'assign' },
      { id: 'tr-inc-8', label: 'Change Priority', fromState: 'In Progress', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Sliders', actionHandler: 'priority' },
      { id: 'tr-inc-9', label: 'Add Note', fromState: 'In Progress', toState: 'In Progress', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-inc-10', label: 'Add Attachment', fromState: 'In Progress', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Paperclip', actionHandler: 'attachment' },
      { id: 'tr-inc-11', label: 'Request Information', fromState: 'In Progress', toState: 'Pending Customer', buttonStyle: 'secondary', icon: 'Mail', actionHandler: 'emailUser', requiresNote: true },
      { id: 'tr-inc-12', label: 'Put on Hold', fromState: 'In Progress', toState: 'Pending Customer', buttonStyle: 'outline', icon: 'Clock', actionHandler: 'hold' },
      { id: 'tr-inc-13', label: 'Escalate', fromState: 'In Progress', toState: 'Escalated', buttonStyle: 'destructive', icon: 'AlertTriangle', actionHandler: 'escalate' },
      { id: 'tr-inc-14', label: 'Resolve', fromState: 'In Progress', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },
      { id: 'tr-inc-15', label: 'Cancel', fromState: 'In Progress', toState: 'Closed', buttonStyle: 'outline', icon: 'X', actionHandler: 'closeFault' },

      // Also support from 'Assigned' directly
      { id: 'tr-inc-7a', label: 'Start Work', fromState: 'Assigned', toState: 'In Progress', buttonStyle: 'primary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-inc-8a', label: 'Change Priority', fromState: 'Assigned', toState: 'Assigned', buttonStyle: 'secondary', icon: 'Sliders', actionHandler: 'priority' },
      { id: 'tr-inc-9a', label: 'Add Note', fromState: 'Assigned', toState: 'Assigned', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-inc-13a', label: 'Escalate', fromState: 'Assigned', toState: 'Escalated', buttonStyle: 'destructive', icon: 'AlertTriangle', actionHandler: 'escalate' },

      // 3. Pending Customer Actions
      { id: 'tr-inc-16', label: 'Send Reminder', fromState: 'Pending Customer', toState: 'Pending Customer', buttonStyle: 'secondary', icon: 'Bell', actionHandler: 'emailUser' },
      { id: 'tr-inc-17', label: 'Resume', fromState: 'Pending Customer', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-inc-18', label: 'Add Note', fromState: 'Pending Customer', toState: 'Pending Customer', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-inc-19', label: 'Resolve', fromState: 'Pending Customer', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },
      { id: 'tr-inc-20', label: 'Cancel', fromState: 'Pending Customer', toState: 'Closed', buttonStyle: 'outline', icon: 'X', actionHandler: 'closeFault' },

      // 4. Pending Vendor Actions
      { id: 'tr-inc-21', label: 'Resume Work', fromState: 'Pending Vendor', toState: 'In Progress', buttonStyle: 'primary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-inc-22', label: 'Add Note', fromState: 'Pending Vendor', toState: 'Pending Vendor', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-inc-23', label: 'Resolve', fromState: 'Pending Vendor', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },

      // 5. Escalated Actions
      { id: 'tr-inc-24', label: 'Investigate Escalation', fromState: 'Escalated', toState: 'In Progress', buttonStyle: 'primary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-inc-25', label: 'Resolve', fromState: 'Escalated', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },
      { id: 'tr-inc-26', label: 'Add Note', fromState: 'Escalated', toState: 'Escalated', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },

      // 6. Resolved Actions
      { id: 'tr-inc-27', label: 'Reopen', fromState: 'Resolved', toState: 'In Progress', buttonStyle: 'secondary', icon: 'RotateCcw', actionHandler: 'reopen' },
      { id: 'tr-inc-28', label: 'Close', fromState: 'Resolved', toState: 'Closed', buttonStyle: 'outline', icon: 'CheckSquare', actionHandler: 'closeFault' },

      // 7. Closed Actions
      { id: 'tr-inc-29', label: 'Reopen', fromState: 'Closed', toState: 'In Progress', buttonStyle: 'secondary', icon: 'RotateCcw', actionHandler: 'reopen' },
      { id: 'tr-inc-30', label: 'View History', fromState: 'Closed', toState: 'Closed', buttonStyle: 'outline', icon: 'Clock', actionHandler: 'history' },
    ],
  },
  {
    id: 'wf-2',
    type: 'Service Request',
    description: 'Formal request from user for access, hardware procurement, or standard service catalog items.',
    initial_status: 'New',
    states: ['New', 'Assigned', 'In Progress', 'Pending Customer', 'Pending Vendor', 'Resolved', 'Closed'],
    transitions: [
      { id: 'tr-sr-1', label: 'Assign', fromState: 'New', toState: 'Assigned', buttonStyle: 'primary', icon: 'UserCheck', actionHandler: 'assign' },
      { id: 'tr-sr-2', label: 'Start Work', fromState: 'New', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-sr-3', label: 'Escalate', fromState: 'New', toState: 'Escalated', buttonStyle: 'destructive', icon: 'AlertTriangle', actionHandler: 'escalate' },
      { id: 'tr-sr-4', label: 'Add Note', fromState: 'New', toState: 'New', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-sr-5', label: 'Request Information', fromState: 'New', toState: 'Pending Customer', buttonStyle: 'warning', icon: 'Mail', actionHandler: 'emailUser', requiresNote: true },
      { id: 'tr-sr-6', label: 'Cancel', fromState: 'New', toState: 'Closed', buttonStyle: 'outline', icon: 'X', actionHandler: 'closeFault' },
      { id: 'tr-sr-7', label: 'Change Assignee', fromState: 'In Progress', toState: 'Assigned', buttonStyle: 'secondary', icon: 'UserPlus', actionHandler: 'assign' },
      { id: 'tr-sr-8', label: 'Resolve', fromState: 'In Progress', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },
      { id: 'tr-sr-9', label: 'Reopen', fromState: 'Resolved', toState: 'In Progress', buttonStyle: 'secondary', icon: 'RotateCcw', actionHandler: 'reopen' },
      { id: 'tr-sr-10', label: 'Close', fromState: 'Resolved', toState: 'Closed', buttonStyle: 'outline', icon: 'CheckSquare', actionHandler: 'closeFault' },
      { id: 'tr-sr-11', label: 'Reopen', fromState: 'Closed', toState: 'In Progress', buttonStyle: 'secondary', icon: 'RotateCcw', actionHandler: 'reopen' },
    ],
  },
  {
    id: 'wf-request',
    type: 'Request',
    description: 'Hardware procurement, purchase orders and requisition lifecycle.',
    initial_status: 'New',
    states: ['New', 'Approved', 'Ordered', 'Received', 'Closed', 'Rejected'],
    transitions: [
      { id: 'tr-req-1', label: 'Approve', fromState: 'New', toState: 'Approved', buttonStyle: 'primary', icon: 'CheckCircle2', actionHandler: 'approve' },
      { id: 'tr-req-2', label: 'Reject', fromState: 'New', toState: 'Rejected', buttonStyle: 'destructive', icon: 'XCircle', actionHandler: 'reject' },
      { id: 'tr-req-3', label: 'Place Order', fromState: 'New', toState: 'Ordered', buttonStyle: 'secondary', icon: 'Play', actionHandler: 'order' },
      { id: 'tr-req-4', label: 'Place Order', fromState: 'Approved', toState: 'Ordered', buttonStyle: 'primary', icon: 'Play', actionHandler: 'order' },
      { id: 'tr-req-5', label: 'Cancel', fromState: 'Approved', toState: 'Closed', buttonStyle: 'outline', icon: 'X', actionHandler: 'cancel' },
      { id: 'tr-req-6', label: 'Mark Received', fromState: 'Ordered', toState: 'Received', buttonStyle: 'primary', icon: 'PackageCheck', actionHandler: 'receive' },
      { id: 'tr-req-7', label: 'Cancel', fromState: 'Ordered', toState: 'Closed', buttonStyle: 'outline', icon: 'X', actionHandler: 'cancel' },
      { id: 'tr-req-8', label: 'Close', fromState: 'Received', toState: 'Closed', buttonStyle: 'outline', icon: 'CheckSquare', actionHandler: 'close' },
      { id: 'tr-req-9', label: 'Reopen', fromState: 'Received', toState: 'Ordered', buttonStyle: 'secondary', icon: 'RotateCcw', actionHandler: 'reopen' },
    ],
  },
  {
    id: 'wf-3',
    type: 'Problem',
    description: 'Underlying cause of one or more recurring incidents requiring root cause investigation.',
    initial_status: 'New',
    states: ['New', 'Assigned', 'In Progress', 'Pending Vendor', 'Resolved', 'Closed'],
    transitions: [
      { id: 'tr-pr-1', label: 'Assign', fromState: 'New', toState: 'Assigned', buttonStyle: 'primary', icon: 'UserCheck', actionHandler: 'assign' },
      { id: 'tr-pr-2', label: 'Start Work', fromState: 'New', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-pr-3', label: 'Add Note', fromState: 'New', toState: 'New', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-pr-4', label: 'Resolve', fromState: 'In Progress', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },
      { id: 'tr-pr-5', label: 'Close', fromState: 'Resolved', toState: 'Closed', buttonStyle: 'outline', icon: 'CheckSquare', actionHandler: 'closeFault' },
      { id: 'tr-pr-6', label: 'Reopen', fromState: 'Closed', toState: 'In Progress', buttonStyle: 'secondary', icon: 'RotateCcw', actionHandler: 'reopen' },
    ],
  },
  {
    id: 'wf-4',
    type: 'Change',
    description: 'ITIL change management workflow with CAB approval, implementation scheduling, and verification.',
    initial_status: 'New',
    states: ['New', 'Assigned', 'In Progress', 'Resolved', 'Closed'],
    transitions: [
      { id: 'tr-ch-1', label: 'Assign', fromState: 'New', toState: 'Assigned', buttonStyle: 'primary', icon: 'UserCheck', actionHandler: 'assign' },
      { id: 'tr-ch-2', label: 'Start Work', fromState: 'New', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-ch-3', label: 'Resolve', fromState: 'In Progress', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },
      { id: 'tr-ch-4', label: 'Close', fromState: 'Resolved', toState: 'Closed', buttonStyle: 'outline', icon: 'CheckSquare', actionHandler: 'closeFault' },
    ],
  },
  {
    id: 'wf-5',
    type: 'Bug Report',
    description: 'Software bug tracking, repro steps verification, patch development, and deployment.',
    initial_status: 'New',
    states: ['New', 'Assigned', 'In Progress', 'Pending Customer', 'Pending Vendor', 'Resolved', 'Closed'],
    transitions: [
      { id: 'tr-bug-1', label: 'Assign', fromState: 'New', toState: 'Assigned', buttonStyle: 'primary', icon: 'UserCheck', actionHandler: 'assign' },
      { id: 'tr-bug-2', label: 'Start Work', fromState: 'New', toState: 'In Progress', buttonStyle: 'secondary', icon: 'Play', actionHandler: 'accept' },
      { id: 'tr-bug-3', label: 'Escalate', fromState: 'New', toState: 'Escalated', buttonStyle: 'destructive', icon: 'AlertTriangle', actionHandler: 'escalate' },
      { id: 'tr-bug-4', label: 'Add Note', fromState: 'New', toState: 'New', buttonStyle: 'outline', icon: 'FileText', actionHandler: 'internalNote', requiresNote: true },
      { id: 'tr-bug-5', label: 'Resolve', fromState: 'In Progress', toState: 'Resolved', buttonStyle: 'success', icon: 'CheckCircle2', actionHandler: 'resolve' },
      { id: 'tr-bug-6', label: 'Close', fromState: 'Resolved', toState: 'Closed', buttonStyle: 'outline', icon: 'CheckSquare', actionHandler: 'closeFault' },
      { id: 'tr-bug-7', label: 'Reopen', fromState: 'Closed', toState: 'In Progress', buttonStyle: 'secondary', icon: 'RotateCcw', actionHandler: 'reopen' },
    ],
  },
];

function loadSavedWorkflows(): Workflow[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0 && (parsed[0].transitions?.length || 0) >= 10) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('Failed to parse saved workflows from localStorage:', err);
  }
  return INITIAL_WORKFLOWS;
}

function saveWorkflowsToStorage(workflows: Workflow[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(workflows));
  } catch (err) {
    console.warn('Failed to save workflows to localStorage:', err);
  }
}

interface WorkflowState {
  workflows: Workflow[];
  selectedWorkflowId: string;
  isLoading: boolean;
  error: string | null;

  // Actions
  setSelectedWorkflowId: (id: string) => void;
  fetchWorkflows: () => Promise<void>;
  updateWorkflow: (id: string, updates: Partial<Workflow>) => Promise<void>;
  addWorkflow: (workflow: Partial<Workflow>) => Promise<Workflow>;
  deleteWorkflow: (id: string) => Promise<void>;
  
  // State management for a workflow
  addState: (workflowId: string, stateName: string) => void;
  updateState: (workflowId: string, stateIndex: number, stateName: string) => void;
  deleteState: (workflowId: string, stateIndex: number) => void;
  moveState: (workflowId: string, stateIndex: number, direction: 'up' | 'down') => void;

  // Transition / Action button management
  addTransition: (workflowId: string, transition: Omit<WorkflowTransition, 'id'>) => void;
  updateTransition: (workflowId: string, transitionId: string, updates: Partial<WorkflowTransition>) => void;
  deleteTransition: (workflowId: string, transitionId: string) => void;

  // Query helpers for Tickets
  getWorkflowForType: (ticketType?: string) => Workflow;
  getAvailableTransitions: (ticketType?: string, currentStatus?: string) => WorkflowTransition[];
  resetToDefaults: () => void;
}

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  workflows: loadSavedWorkflows(),
  selectedWorkflowId: 'wf-1',
  isLoading: false,
  error: null,

  setSelectedWorkflowId: (id: string) => {
    set({ selectedWorkflowId: id });
  },

  fetchWorkflows: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/support/settings/workflows');
      const data = response.data?.data || response.data;
      if (Array.isArray(data) && data.length > 0) {
        const mappedWorkflows: Workflow[] = data.map((item: any) => {
          if (Array.isArray(item.steps) && item.steps.length > 0) {
            const transitions: WorkflowTransition[] = [];
            item.steps.forEach((step: any) => {
              if (Array.isArray(step.actions)) {
                step.actions.forEach((act: any) => {
                  transitions.push({
                    id: act.id,
                    label: act.buttonName || act.name,
                    fromState: step.name,
                    toState: act.targetStepName || (act.targetStepId === 'remain' ? step.name : 'In Progress'),
                    buttonStyle: act.buttonStyle || 'secondary',
                    icon: act.icon,
                    requiresNote: act.requiresNote,
                  });
                });
              }
            });
            return {
              id: item.id,
              type: item.ticketType || item.name,
              description: item.description || '',
              initial_status: item.steps[0]?.name || 'New',
              states: item.steps.map((s: any) => s.name),
              transitions: transitions.length > 0 ? transitions : INITIAL_WORKFLOWS[0].transitions,
              isDefault: item.isDefault,
              createdAt: item.createdAt,
              updatedAt: item.updatedAt,
            };
          }
          return item;
        });
        set({ workflows: mappedWorkflows, isLoading: false });
        saveWorkflowsToStorage(mappedWorkflows);
        return;
      }
    } catch {
      // Offline fallback: keep localStorage data or initial
    }
    const saved = loadSavedWorkflows();
    set({ workflows: saved, isLoading: false });
  },

  updateWorkflow: async (id: string, updates: Partial<Workflow>) => {
    const nextWorkflows = get().workflows.map((w) =>
      w.id === id ? { ...w, ...updates, updatedAt: new Date().toISOString() } : w
    );
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);

    try {
      await apiClient.patch(`/support/settings/workflows/${id}`, updates);
    } catch {
      // Offline fallback maintained in localStorage
    }
    toast.success('Workflow updated successfully');
  },

  addWorkflow: async (workflowData: Partial<Workflow>) => {
    const newId = `wf-${Date.now()}`;
    const newWf: Workflow = {
      id: newId,
      type: workflowData.type || 'Custom Workflow',
      description: workflowData.description || '',
      initial_status: workflowData.initial_status || 'New',
      states: workflowData.states && workflowData.states.length > 0 ? workflowData.states : ['New', 'In Progress', 'Closed'],
      transitions: workflowData.transitions || [
        { id: `tr-${Date.now()}-1`, label: 'Accept', fromState: 'New', toState: 'In Progress', buttonStyle: 'primary' },
        { id: `tr-${Date.now()}-2`, label: 'Close', fromState: 'In Progress', toState: 'Closed', buttonStyle: 'success' },
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const nextWorkflows = [...get().workflows, newWf];
    set({ workflows: nextWorkflows, selectedWorkflowId: newId });
    saveWorkflowsToStorage(nextWorkflows);

    try {
      await apiClient.post('/support/settings/workflows', newWf);
    } catch {
      // Saved in client storage
    }
    toast.success(`Created workflow "${newWf.type}"`);
    return newWf;
  },

  deleteWorkflow: async (id: string) => {
    const remaining = get().workflows.filter((w) => w.id !== id);
    if (remaining.length === 0) {
      toast.error('Cannot delete the only remaining workflow');
      return;
    }
    set({
      workflows: remaining,
      selectedWorkflowId: remaining[0].id,
    });
    saveWorkflowsToStorage(remaining);

    try {
      await apiClient.delete(`/support/settings/workflows/${id}`);
    } catch {
      // Saved in client storage
    }
    toast.success('Workflow deleted');
  },

  addState: (workflowId: string, stateName: string) => {
    const trimmed = stateName.trim();
    if (!trimmed) return;
    const nextWorkflows = get().workflows.map((w) => {
      if (w.id === workflowId) {
        if (w.states.includes(trimmed)) {
          toast.error(`State "${trimmed}" already exists`);
          return w;
        }
        return {
          ...w,
          states: [...w.states, trimmed],
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);
    toast.success(`Added state "${trimmed}"`);
  },

  updateState: (workflowId: string, stateIndex: number, stateName: string) => {
    const trimmed = stateName.trim();
    if (!trimmed) return;
    const nextWorkflows = get().workflows.map((w) => {
      if (w.id === workflowId) {
        const oldState = w.states[stateIndex];
        const newStates = [...w.states];
        newStates[stateIndex] = trimmed;
        const newTransitions = w.transitions.map((tr) => ({
          ...tr,
          fromState: tr.fromState === oldState ? trimmed : tr.fromState,
          toState: tr.toState === oldState ? trimmed : tr.toState,
        }));
        return {
          ...w,
          states: newStates,
          transitions: newTransitions,
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);
  },

  deleteState: (workflowId: string, stateIndex: number) => {
    const nextWorkflows = get().workflows.map((w) => {
      if (w.id === workflowId) {
        if (w.states.length <= 1) {
          toast.error('Workflow must have at least one state');
          return w;
        }
        const stateToDelete = w.states[stateIndex];
        const newStates = w.states.filter((_, idx) => idx !== stateIndex);
        const newTransitions = w.transitions.filter(
          (tr) => tr.fromState !== stateToDelete && tr.toState !== stateToDelete
        );
        return {
          ...w,
          states: newStates,
          transitions: newTransitions,
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);
    toast.success('State removed');
  },

  moveState: (workflowId: string, stateIndex: number, direction: 'up' | 'down') => {
    const nextWorkflows = get().workflows.map((w) => {
      if (w.id === workflowId) {
        const newStates = [...w.states];
        const targetIndex = direction === 'up' ? stateIndex - 1 : stateIndex + 1;
        if (targetIndex < 0 || targetIndex >= newStates.length) return w;
        const [moved] = newStates.splice(stateIndex, 1);
        newStates.splice(targetIndex, 0, moved);
        return {
          ...w,
          states: newStates,
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);
  },

  addTransition: (workflowId: string, transition: Omit<WorkflowTransition, 'id'>) => {
    const newTransition: WorkflowTransition = {
      ...transition,
      id: `tr-${Date.now()}-${Math.floor(secureRandom() * 1000)}`,
    };
    const nextWorkflows = get().workflows.map((w) => {
      if (w.id === workflowId) {
        return {
          ...w,
          transitions: [...(w.transitions || []), newTransition],
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);
    toast.success(`Action button "${transition.label}" added`);
  },

  updateTransition: (workflowId: string, transitionId: string, updates: Partial<WorkflowTransition>) => {
    const nextWorkflows = get().workflows.map((w) => {
      if (w.id === workflowId) {
        return {
          ...w,
          transitions: (w.transitions || []).map((tr) =>
            tr.id === transitionId ? { ...tr, ...updates } : tr
          ),
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);
  },

  deleteTransition: (workflowId: string, transitionId: string) => {
    const nextWorkflows = get().workflows.map((w) => {
      if (w.id === workflowId) {
        return {
          ...w,
          transitions: (w.transitions || []).filter((tr) => tr.id !== transitionId),
          updatedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    set({ workflows: nextWorkflows });
    saveWorkflowsToStorage(nextWorkflows);
    toast.success('Action button removed');
  },

  getWorkflowForType: (ticketType?: string): Workflow => {
    const typeKey = normalizeTicketType(ticketType);
    const { workflows } = get();
    
    const match = workflows.find((w) => {
      const wKey = normalizeTicketType(w.type);
      return wKey === typeKey;
    });

    return match || workflows[0] || INITIAL_WORKFLOWS[0];
  },

  getAvailableTransitions: (ticketType?: string, currentStatus?: string): WorkflowTransition[] => {
    const wf = get().getWorkflowForType(ticketType);
    if (!wf || !wf.transitions) return [];

    const normCurrent = normalizeStateCategory(currentStatus);
    const rawNorm = (currentStatus || 'new').toLowerCase().replace(/[^a-z0-9]/g, '');

    // Return transitions matching fromState, category matching, or wildcard '*'
    const matching = wf.transitions.filter((tr) => {
      if (!tr.fromState || tr.fromState === '*') {
        // Universal action: on closed/resolved tickets, only show reopen and internal note
        if (normCurrent === 'closed') {
          return tr.label.toLowerCase().includes('reopen') || tr.label.toLowerCase().includes('note');
        }
        if (normCurrent === 'resolved') {
          return tr.label.toLowerCase().includes('close') || tr.label.toLowerCase().includes('reopen') || tr.label.toLowerCase().includes('note');
        }
        return true;
      }
      const rawFrom = tr.fromState.toLowerCase().replace(/[^a-z0-9]/g, '');
      const normFrom = normalizeStateCategory(tr.fromState);
      return rawFrom === rawNorm || normFrom === normCurrent;
    });

    if (matching.length > 0) return matching;

    // Fallback: Return wildcard transitions
    const fallbacks = wf.transitions.filter((tr) => tr.fromState === '*' || normalizeStateCategory(tr.fromState) === normCurrent);
    return fallbacks.length > 0 ? fallbacks : wf.transitions.slice(0, 4);
  },

  resetToDefaults: () => {
    set({ workflows: INITIAL_WORKFLOWS, selectedWorkflowId: 'wf-1' });
    saveWorkflowsToStorage(INITIAL_WORKFLOWS);
    toast.success('Workflows reset to default template');
  },
}));
